import React, { useState, useEffect } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import AlertsTab from "./AlertsTab";
import FavoritesTab from "./FavoritesTab";
import ProviderMatchesTab from "./ProviderMatchesTab";
import MessagesTab from "./MessagesTab";
import PatientPreferenceTab from "./PatientPreferenceTab";
import { PatientAppointmentsTab } from "./PatientAppointmentsTab";
import PatientWaitlistTab from "./PatientWaitlistTab";
import PatientInformation from "./PatientInformation";
import PatientDocumentsTab from "./PatientDocumentsTab";
import ActiveProvidersTab from "./ActiveProvidersTab";
import PatientReferralsTab from "./PatientReferralsTab";
import { getPatient } from "../APIs/patientService";
import { useCurrentUser } from "../APIs/UserContext";
import Header from "../modals/Header.js";

const tabs = [
  "profile",
  "documents",
  "messages",
  "active providers",
  "appointments",
  "referrals",
  "provider matches",
  "favorites",
  "alerts",
  "patient preference",
  "waitlisted appointments",
];

export default function PatientProfile() {
  // Remove propPatientId prop
  const { currentUser, loading: userLoading } = useCurrentUser(); // Add this
  const { patientId: routePatientId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");

  // 🔹 Canonical patient id for this page
  // Priority: context user → route param
  const effectivePatientId =
    currentUser?.patientId ?? (routePatientId ? Number(routePatientId) : null);

  const [activeTab, setActiveTab] = useState(tabFromUrl || "profile");
  const [patientInfo, setPatientInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch patient data
  useEffect(() => {
    const fetchPatient = async () => {
      // Wait for user context to load first
      if (userLoading) {
        return;
      }

      if (!effectivePatientId) {
        console.error("❌ No patientId available");
        setLoading(false);
        return;
      }

      try {
        const data = await getPatient(effectivePatientId);

        // Transform API data to match display structure
        const hasName = data.firstName || data.lastName;
        setPatientInfo({
          name: hasName
            ? `${data.firstName || ""} ${data.lastName || ""}`.trim()
            : null,
          email: data.email,
          dob: data.dateOfBirth,
          id: `P${data.patientId}`,
          avatar:
            "https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png",
          fullData: data,
        });
      } catch (error) {
        console.error("❌ Error fetching patient:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPatient();
  }, [effectivePatientId, userLoading]); // Add userLoading dependency

  // Update URL when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // Handle URL parameter changes (e.g., when navigating back/forward)
  useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl, activeTab]);

  // Loading state - wait for both user context and patient data
  if (userLoading || loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <p className="text-gray-500">Loading patient profile...</p>
        </div>
      </>
    );
  }

  // Error state
  if (!patientInfo) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 font-semibold">Patient not found</p>
            {!effectivePatientId && (
              <p className="text-sm text-gray-500 mt-2">
                No patient ID available. Please contact support.
              </p>
            )}
          </div>
        </div>
      </>
    );
  }

  const renderTabComponent = (tab) => {
    switch (tab) {
      case "profile":
        return <PatientInformation patientId={effectivePatientId} />;
      case "documents":
        return <PatientDocumentsTab patientId={effectivePatientId} />;
      case "messages":
        return <MessagesTab patientId={effectivePatientId} />;
      case "active providers":
        return <ActiveProvidersTab patientId={effectivePatientId} />;
      case "appointments":
        return <PatientAppointmentsTab patientId={effectivePatientId} />;
      case "provider matches":
        return <ProviderMatchesTab patientId={effectivePatientId} />;
      case "favorites":
        return <FavoritesTab patientId={effectivePatientId} />;
      case "alerts":
        return <AlertsTab />;
      case "patient preference":
        return (
          <PatientPreferenceTab
            patientId={effectivePatientId}
            switchTab={handleTabChange}
          />
        );
      case "referrals":
        return <PatientReferralsTab patientId={effectivePatientId} />;
      case "waitlisted appointments":
        return <PatientWaitlistTab patientId={effectivePatientId} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Profile Header Strip */}
      <div className="bg-gradient-to-br from-teal-50 to-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center gap-5">
          <img
            src={patientInfo.avatar}
            alt="Patient Avatar"
            className="w-20 h-20 rounded-full ring-2 ring-teal-400 object-cover shrink-0"
          />
          <div>
            {patientInfo.name && (
              <h2 className="text-2xl font-bold text-gray-900">
                {patientInfo.name}
              </h2>
            )}
            <p className="text-sm text-gray-500 mt-0.5">
              DOB: {patientInfo.dob}
            </p>
            <p className="text-sm text-gray-500">Email: {patientInfo.email}</p>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="sticky top-0 bg-white z-10 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-2 overflow-x-auto py-3 scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`px-4 py-2 rounded-full capitalize whitespace-nowrap text-sm font-medium transition-colors ${
                  activeTab === tab
                    ? "bg-teal-600 text-white"
                    : "bg-white border border-gray-200 text-gray-600 hover:border-teal-300 hover:text-teal-600"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -30 }}
          transition={{ duration: 0.3 }}
        >
          {renderTabComponent(activeTab)}
        </motion.div>
      </div>
    </div>
  );
}
